




































































































import _ from 'lodash';
import axios from 'axios';
import dayjs from 'dayjs';
import { DataPagination } from 'vuetify';
import { formatDateForHumanSpecies } from '@/utils/time';

import {
  Component, Mixins, Prop, Watch,
} from 'vue-property-decorator';

import {
  like, and, or, exact,
} from '@/classes/pimcore-filtering/Filters';

import CustomSelect from '@/components/FormBuilder/CustomSelect.vue';

import FilterBuilder from '@/classes/pimcore-filtering/FilterBuilder';
import UserMixin from '@/mixins/UserMixin';
import EntityHelperMixin from '@/mixins/EntityHelperMixin';

import DateRange from '@/components/common/DateRange.vue';

import { graphqlEndpoints } from '@/utils/config';
import { Roles } from '@/store/profile/types';

const DATE_FORMAT = 'YYYY-MM-DD';

@Component({
  components: {
    DateRange,
    CustomSelect,
  },
})
export default class ApplicationActualsList extends Mixins(UserMixin, EntityHelperMixin) {
  @Prop({
    type: String,
    required: false,
    default: 'ApplicationActualsList',
  }) readonly localStoragePrefix!: string;

  data: Array<any> = [];

  clientsList: Array<any> = [];

  statusesList: Array<any> = [];

  coordinatorsList: Array<any> = [];

  platformsList: Array<any> = [];

  selectedPlatform: any = null;

  defaultPlatform: any = null;

  coordinatorRole: any = null;

  selectedCoordinator: any = null;

  selectedClient: any = null;

  loading = true;

  total = 0;

  defaultDateFrom: any = null;

  defaultDateTo: any = null;

  pagination: DataPagination = {
    page: 1,
    pageStart: 0,
    itemsPerPage: 10,
    pageStop: 0,
    pageCount: 0,
    itemsLength: 0,
  };

  async beforeMount() {
    this.loading = true;

    this.calculateDefaultDates();
    this.statusesList = await this.loadStatuses(['in_progress', 'actual_data_filled']);
    this.platformsList = await this.loadPlatforms();

    this.defaultPlatform = this.userPlatforms[0];
    const selectedPlatformId = this.getFromStorage('platform');
    if (selectedPlatformId) {
      this.selectedPlatform = this.userPlatforms.find((platform: any) => platform.id === selectedPlatformId);
    }
    if (!this.selectedPlatform) {
      this.selectedPlatform = this.defaultPlatform;
    }

    this.clientsList = await this.loadClients(this.selectedPlatform);
    const selectedClientId = this.getFromStorage('client');
    if (selectedClientId) {
      this.selectedClient = this.clientsList.find((client: any) => client.id === selectedClientId);
    }

    this.coordinatorRole = (await this.loadRoles()).find((v: any) => v.role_type === Roles.COORDINATOR);
    this.coordinatorsList = await this.loadUsersByRole(this.coordinatorRole, this.selectedPlatform);

    const selectedCoordinatorId = this.getFromStorage('coordinator');
    if (selectedCoordinatorId) {
      this.selectedCoordinator = this.coordinatorsList.find((coordinator: any) => coordinator.id === selectedCoordinatorId);
    }

    await this.search();
  }

  calculateDefaultDates() {
    const dateNow = new Date();
    const dateAgo = new Date();
    dateAgo.setMonth(dateAgo.getMonth() - 1);
    dateAgo.setDate(1);
    this.defaultDateFrom = dateAgo.toISOString().split('T')[0];
    this.defaultDateTo = dateNow.toISOString().split('T')[0];
  }

  loadStatuses(statusCodes: Array<string>) {
    const filterBuilder = new FilterBuilder();
    const orLikes: Array<any> = [];
    for (let i = 0; i < statusCodes.length; i += 1) {
      orLikes.push(like('code', statusCodes[i]));
    }
    filterBuilder.addFilters(and(or(...orLikes)));

    return this.loadEntityListing('StatusApplication', {
      filter: filterBuilder.build(),
    });
  }

  loadPlatforms() {
    const filterBuilder = new FilterBuilder();
    const ors: any[] = this.userPlatformIds.map((platformId: number) => exact('oo_id', platformId));
    filterBuilder.addFilters(or(...ors));
    return this.loadEntityListing('PlantsHR', {
      filter: filterBuilder.build(),
    }, 'network-only', {
      maxDepth: 1,
    });
  }

  async loadRoles() {
    return this.loadEntityListing('Role', {}, 'network-only', { maxDepth: 1 });
  }

  async loadClients(platform: any) {
    const filterBuilder = new FilterBuilder();
    filterBuilder.addFilters(and(
      like('Plants', `%${platform.id}%`),
    ));
    return this.loadEntityListing(
      'Client',
      { filter: filterBuilder.build() },
      'network-only',
      { maxDepth: 1 },
    );
  }

  loadUsersBySuperiorID(superior_id: string) {
    return this.loadEntityListing('User', {
      filter: (new FilterBuilder()).addFilters(and(like('superior__id', superior_id))).build(),
    }, 'network-only', {
      maxDepth: 1,
    });
  }

  loadUsersByRole(role: any, platform: any) {
    const filterBuilder = new FilterBuilder();
    filterBuilder.addFilters(and(
      like('role__id', role.id),
      like('PlantsHR', `%${platform.id}%`),
    ));
    return this.loadEntityListing('User', {
      filter: filterBuilder.build(),
    }, 'network-only', {
      maxDepth: 1,
    });
  }

  formatDateForHumanSpecies(value: string) {
    return formatDateForHumanSpecies(value);
  }

  get headers() {
    return [
      {
        text: this.$t('date_from'),
        value: 'date_from',
      },
      {
        text: this.$t('date_to'),
        value: 'date_to',
      },
      {
        text: this.$t('client'),
        value: 'client',
      },
      {
        text: this.$t('coordinator'),
        value: 'coordinator',
      },
      {
        text: this.$t('platform'),
        value: 'platform',
      },
      {
        text: this.$t('status'),
        value: 'status',
      },
    ];
  }

  get applicationDateFrom() {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return this.$route.query.dateFrom ? new Date(this.$route.query.dateFrom.toString()) : date;
  }

  get applicationDateTo() {
    return this.$route.query.dateTo ? new Date(this.$route.query.dateTo.toString()) : new Date();
  }

  async onPagination(pagination: DataPagination) {
    if (pagination.itemsPerPage === this.pagination.itemsPerPage && pagination.pageStart === this.pagination.pageStart) {
      return;
    }
    this.pagination = pagination;
    await this.search();
  }

  findBy(value: any, arr: Array<any>) {
    if (!value) {
      return {};
    }
    return arr.find((v: any) => v.id.toString() === value.id.toString()) || {};
  }

  async search() {
    this.loading = true;
    const params = new URLSearchParams();
    params.append('dateFrom', dayjs(this.applicationDateFrom).format(DATE_FORMAT));
    params.append('dateTo', dayjs(this.applicationDateTo).format(DATE_FORMAT));
    params.append('pageSize', this.pagination.itemsPerPage.toString());
    params.append('pageFrom', (this.pagination.page - 1).toString());
    if (this.selectedCoordinator) {
      params.append('coordinatorIds', this.selectedCoordinator.id);
    } else {
      params.append('coordinatorIds', this.coordinatorsList.map((v: any) => v.id).join(','));
    }
    if (this.selectedClient) {
      params.append('clientIds', this.selectedClient.id);
    }
    params.append('statusIds', this.statusesList.map((v: any) => v.id).join(','));
    params.append('platformId', this.selectedPlatform?.id);

    let resp: any = null;
    try {
      resp = await axios.get(graphqlEndpoints.getAggregatedApplications, {
        params,
      });
    } catch (e) {
      console.error(e);
      this.loading = false;
    }
    this.data = resp?.data?.result?.applications || [];
    this.total = resp?.data?.result?.total || 0;
    this.loading = false;
  }

  @Watch('$route')
  routeChanged() {
    if (this.selectedPlatform) {
      this.search();
    }
  }

  setFilter(name: string, filter: any) {
    const routeName = this?.$route?.name || 'ApplicationActualsList';
    const query = _.cloneDeep(this.$route.query);
    query[name] = filter;
    this.$router.push({
      name: routeName,
      query,
    });
  }

  onRowClick(row: any) {
    this.$router.push({
      name: 'ApplicationEditActual',
      query: {
        dateFrom: row.date_from,
        dateTo: row.date_to,
        statusId: row.status.id,
        coordinatorId: row.coordinator.id,
        platformId: row.platform.id,
        clientId: row.client.id,
      },
    });
  }

  async coordinatorChange(value: any) {
    this.selectedCoordinator = value;
    this.setToStorage('coordinator', value.id);

    await this.search();
  }

  async clientChange(value: any) {
    this.selectedClient = value;
    this.setToStorage('client', value.id);

    await this.search();
  }

  async platformChange(value: any) {
    this.selectedPlatform = value;
    this.setToStorage('platform', value.id);

    this.coordinatorsList = await this.loadUsersByRole(this.coordinatorRole, this.selectedPlatform);
    this.selectedCoordinator = null;
    this.setToStorage('coordinator', '');

    this.clientsList = await this.loadClients(this.selectedPlatform);
    this.selectedClient = null;
    this.setToStorage('client', '');

    await this.search();
  }

  filtersChanged(): boolean {
    return this.selectedCoordinator
      || this.selectedClient
      || this.selectedPlatform?.id !== this.defaultPlatform?.id
      || this.$route.query.dateFrom !== this.defaultDateFrom
      || this.$route.query.dateTo !== this.defaultDateTo;
  }

  clearFilters() {
    this.platformChange(this.defaultPlatform);
    this.$bus.$emit('clear-filters');
  }
}

